import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';

import { Buyer } from '@ps/core-models';
import { PlatformSwitcherRouteKey } from '@ps/platform-switcher-data-access';
import {
  AdvancedFeasibilityFilters,
  DateRangeFilterParams,
  FindSurveysParams,
  LaunchToggleField,
  PatchableMetaData,
  PsidLink,
  RespondentCountsParams,
  RespondentFiltersRequest,
  ScheduleLaunch,
  SearchCriteriaSurveysParams,
  SearchGroupSurveysParams,
  LaunchSameSettings,
  Survey,
  SurveyLaunchFormState,
  SurveyLocaleData,
  SurveyMarginFormState,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
  TrackerConfig,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';
import { Option } from '@sapphire/result';
import { SurveyStatus } from '@ps/survey-kernel';

import {
  SurveyCreationCpiPricing,
  SurveyCreationInsightsActions,
  SurveyCreationInsightsPreDraftActions,
  AdvancedFilterActions,
} from './insights';
import { OfferPriceApply } from './insights/utils/survey-creation-apply-offer-price.types';

import { SurveyChildKey } from './survey-creation-shared-state.model';
import {
  CHILD_SCOPE,
  SurveyChildScope,
} from './survey-creation-child-registry.helper';
import {
  SurveyCreationCoreActions,
  SurveyCreationCoreChildrenActions,
} from './core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationChildrenActions,
  SurveyCreationMcActions,
  SurveyCreationPreDraftActions,
  SurveyCreationTrackerActions,
} from './audience/survey-creation-audience.actions';
import { SurveyCreationReferenceActions } from './reference/survey-creation-reference.actions';
import {
  LaunchPricingConfirmation,
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './launch/survey-creation-launch.actions';
import { SuppliersMetaData } from './audience/survey-creation-suppliers-meta-data-state.model';

@Injectable({ providedIn: 'root' })
export class SurveyCreationActionsFacade {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);

  // forceReload refetches the survey even when it is already in memory.
  public initAudience(
    surveyId: Nullable<number>,
    activeChildKey: Option<SurveyChildKey> = Option.none,
    forceReload = false,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.init({
        surveyId,
        forceReload,
        ...activeChildKeyProp(activeChildKey),
      }),
    );
  }

  public initLaunch(
    surveyId: Nullable<number>,
    activeChildKey: Option<SurveyChildKey> = Option.none,
    forceReload = false,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.init({
        surveyId,
        forceReload,
        ...activeChildKeyProp(activeChildKey),
      }),
    );
  }

  public setActiveChildKey(activeChildKey: Nullable<SurveyChildKey>): void {
    this._store.dispatch(
      SurveyCreationCoreActions.setActiveChildKey({ activeChildKey }),
    );
  }

  public buildPreDraftChildren(localeData: SurveyLocaleData[]): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.buildPreDraftChildren({ localeData }),
    );
  }

  public addSurveyChild(locale: SurveyLocaleData): void {
    this._store.dispatch(SurveyCreationMcActions.addSurveyChild(locale));
  }

  public deleteSurveyChild(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationMcActions.deleteSurveyChild({ surveyId }),
    );
  }

  public applyTemplateToMcSurvey(
    templateId: number,
    localization: string,
    replacedSurveyId: Option<number>,
  ): void {
    this._store.dispatch(
      SurveyCreationMcActions.applyTemplateToMcSurvey({
        templateId,
        localization,
        ...replacedSurveyId.mapOr({}, (surveyId) => ({
          replacedSurveyId: surveyId,
        })),
      }),
    );
  }

  public createMcTraditionalSurveys(): void {
    this._store.dispatch(SurveyCreationMcActions.createMcTraditionalSurveys());
  }

  public createMcGroupedProject(): void {
    this._store.dispatch(SurveyCreationMcActions.createMcGroupedProject());
  }

  public createTrackerProject(): void {
    this._store.dispatch(SurveyCreationTrackerActions.createTrackerProject());
  }

  public addWave(): void {
    this._store.dispatch(SurveyCreationTrackerActions.addWave());
  }

  public deleteWave(surveyId: number): void {
    this._store.dispatch(SurveyCreationTrackerActions.deleteWave({ surveyId }));
  }

  public syncTrackerProject(): void {
    this._store.dispatch(SurveyCreationTrackerActions.syncProject());
  }

  public updateLaunchDate(
    surveyId: number,
    scheduleLaunch: ScheduleLaunch,
  ): void {
    this._store.dispatch(
      SurveyCreationTrackerActions.updateLaunchDate({
        surveyId,
        scheduleLaunch,
      }),
    );
  }

  public selectAllPreDraftChildren(): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.selectAllPreDraftChildren(),
    );
  }

  public clearAllPreDraftChildren(): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.clearAllPreDraftChildren(),
    );
  }

  public selectPreDraftChildren(ids: SurveyChildKey[]): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.selectPreDraftChildren({ ids }),
    );
  }

  public selectChild(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationChildrenActions.selectChild({ surveyId }),
    );
  }

  public updateChildSurveyCpi(surveyId: number, cpi: number): void {
    this._store.dispatch(
      SurveyCreationChildrenActions.updateChildSurveyCpi({ surveyId, cpi }),
    );
  }

  public patchChildMetadata(
    changes: PatchableMetaData,
    scope: SurveyChildScope,
  ): void {
    this._store.dispatch(
      SurveyCreationChildrenActions.patchChildMetadata({ changes, scope }),
    );
  }

  public switchChild(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationCoreChildrenActions.switchChild({ surveyId }),
    );
  }

  public createStandardSurvey(): void {
    this._store.dispatch(SurveyCreationAudienceActions.createStandardSurvey());
  }

  public changeCreatedSurveyLocale(
    locale: SurveyLocaleData,
    deleteCurrent: boolean,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.changeCreatedSurveyLocale({
        locale,
        deleteCurrent,
      }),
    );
  }

  public updateMetadata(
    metadata: SurveyMetadataFormState,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateMetadata({ metadata, scope }),
    );
  }

  public patchChildrenMetadataByKey(
    changesByKey: ReadonlyMap<SurveyChildKey, PatchableMetaData>,
  ): void {
    changesByKey.forEach((changes, key) =>
      this.patchChildMetadata(changes, CHILD_SCOPE.keys([key])),
    );
  }

  public updateMargin(margin: SurveyMarginFormState): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateMargin({ margin }),
    );
  }

  public settleBuyerCpi(): void {
    this._store.dispatch(SurveyCreationAudienceActions.settleBuyerCpi());
  }

  public settleTargetMargin(targetMargin: number): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.settleTargetMargin({ targetMargin }),
    );
  }

  public confirmHighCpi(cpi: number): void {
    this._store.dispatch(SurveyCreationAudienceActions.confirmHighCpi({ cpi }));
  }

  public selectTags(tagIds: number[]): void {
    this._store.dispatch(SurveyCreationAudienceActions.selectTags({ tagIds }));
  }

  public updateQuotas(
    quotas: SurveyQuotasFormState,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateQuotas({ quotas, scope }),
    );
  }

  public updateQualifications(
    qualifications: SurveyQualificationsFormState,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.updateQualifications({
        qualifications,
        scope,
      }),
    );
  }

  public updateLaunchForm(form: SurveyLaunchFormState): void {
    this._store.dispatch(SurveyCreationLaunchActions.updateForm({ form }));
  }

  public updateLaunchSameSettings(sameSettings: LaunchSameSettings): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.updateSameSettings({ sameSettings }),
    );
  }

  public patchActiveSurvey(
    patch: Partial<Survey>,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationCoreActions.patchActiveSurvey({ patch, scope }),
    );
  }

  public saveSurvey(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveSurvey({}));
  }

  public createSurveyFromTemplate(
    templateId: number,
    deleteSurveyId: Nullable<number> = null,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.createSurveyFromTemplate({
        templateId,
        deleteSurveyId,
      }),
    );
  }

  public createMcSurveysFromTemplate(
    mcTemplateId: string,
    deleteSurveyIds: number[],
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.createMcSurveysFromTemplate({
        mcTemplateId,
        deleteSurveyIds,
      }),
    );
  }

  public saveAndCloneSurvey(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndCloneSurvey());
  }

  public saveAndCreateNew(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndCreateNew());
  }

  public saveAndLaunch(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndLaunch());
  }

  public saveAsLive(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAsLive());
  }

  public saveAndEditAudience(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndEditAudience());
  }

  /** `uniqueLinkPsidLinks` is null unless in Include + Unique Buyer Entry Links mode. */
  public requestTestEntryLink(
    uniqueLinkPsidLinks: Nullable<PsidLink[]> = null,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.requestTestEntryLink({ uniqueLinkPsidLinks }),
    );
  }

  public saveAndOpenManageSurvey(updateSupplier: boolean): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveAndOpenManageSurvey({ updateSupplier }),
    );
  }

  public checkBillingCodeAlreadyUsed(billingId: string): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.checkBillingCodeAlreadyUsed({ billingId }),
    );
  }

  public loadAidaEligibility(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.loadAidaEligibility({ surveyId }),
    );
  }

  public loadLaunchPricing(): void {
    this._store.dispatch(SurveyCreationLaunchActions.loadLaunchPricing());
  }

  public launchSurvey(
    status: SurveyStatus,
    pricingConfirmations: readonly LaunchPricingConfirmation[],
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.launchSurvey({
        status,
        pricingConfirmations,
      }),
    );
  }

  // "Launch all Surveys": moves one non-active sibling locale straight to
  // `status` by its own id. Resolves true/false instead of the usual
  // dispatch-only void — there is no action the caller can itself await to
  // sequence one locale's launch after another's, so this facade (which
  // already knows the action types) is the narrowest place to bridge that,
  // same reasoning as saveActiveChildSilently below.
  public launchSiblingSurvey(
    surveyId: number,
    status: SurveyStatus,
  ): Observable<boolean> {
    this._store.dispatch(
      SurveyCreationLaunchActions.launchSiblingSurvey({ surveyId, status }),
    );
    return this._actions$.pipe(
      ofType(
        SurveyCreationLaunchApiActions.launchSurveySuccess,
        SurveyCreationLaunchApiActions.launchSurveyFailure,
      ),
      take(1),
      map(
        (action: Action) =>
          action.type ===
          SurveyCreationLaunchApiActions.launchSurveySuccess.type,
      ),
    );
  }

  public finishLaunchAll(): void {
    this._store.dispatch(SurveyCreationLaunchActions.finishLaunchAll());
  }

  // "Launch all Surveys": patches whatever the buyer is currently editing on
  // the active locale's own launch settings before any locale (including
  // this one) is launched — a narrow PATCH (see
  // SurveyCreationLaunchActions.patchLaunchSettings), not the full survey
  // save pipeline, whose validation gate can silently drop an invalid save.
  public saveActiveChildSilently(): Observable<boolean> {
    this._store.dispatch(SurveyCreationLaunchActions.patchLaunchSettings());
    return this._actions$.pipe(
      ofType(
        SurveyCreationLaunchApiActions.patchLaunchSettingsSuccess,
        SurveyCreationLaunchApiActions.patchLaunchSettingsFailure,
      ),
      take(1),
      map(
        (action: Action) =>
          action.type ===
          SurveyCreationLaunchApiActions.patchLaunchSettingsSuccess.type,
      ),
    );
  }

  // Dispatch for SurveyCreationWorkflowExitEffects — leaving the survey
  // workflow, with or without saving, with or without an impersonation change.
  public saveAndExit(): void {
    this._store.dispatch(SurveyCreationCoreActions.saveAndExit());
  }

  public discardSurveyChanges(): void {
    this._store.dispatch(SurveyCreationCoreActions.discardSurveyChanges());
  }

  public saveSurveyAndDisconnectImpersonation(): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurveyAndDisconnectImpersonation(),
    );
  }

  public saveSurveyAndSwitchImpersonatedBuyer(buyer: Buyer): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurveyAndSwitchImpersonatedBuyer({ buyer }),
    );
  }

  public saveSurveyAndSwitchToLegacy(
    routeKey: PlatformSwitcherRouteKey,
    params: Record<string, string | number>,
  ): void {
    this._store.dispatch(
      SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy({
        routeKey,
        params,
      }),
    );
  }

  public reset(): void {
    this._store.dispatch(SurveyCreationCoreActions.reset());
  }

  // Dispatch for the Tracker Configuration Drawer's confirm.
  public setupTrackerConfig(config: TrackerConfig): void {
    this._store.dispatch(
      SurveyCreationCoreActions.setupTrackerConfig({ config }),
    );
  }

  // Dispatch for the Tracker banner's "Reset to Standard Survey" link.
  public resetTrackerConfig(): void {
    this._store.dispatch(SurveyCreationCoreActions.resetTrackerConfig());
  }

  public uploadLocationCodes(
    countryCode: string,
    qualificationCode: number,
    qualificationName: string,
    file: File,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.uploadLocationCodes({
        countryCode,
        qualificationCode,
        qualificationName,
        file,
      }),
    );
  }

  public downloadLocationCodesTemplate(
    countryCode: string,
    qualificationCode: number,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.downloadLocationCodesTemplate({
        countryCode,
        qualificationCode,
      }),
    );
  }

  public downloadValidatedLocationFile(qualificationCode: number): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.downloadValidatedLocationFile({
        qualificationCode,
      }),
    );
  }

  public downloadEntryLinksTemplate(): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.downloadEntryLinksTemplate(),
    );
  }

  public updateTrackerWaveExclusions(waveExclusions: number[]): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.updateTrackerWaveExclusions({
        waveExclusions,
      }),
    );
  }

  public updateBasicToken(enable: boolean, value: string): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.updateBasicToken({ enable, value }),
    );
  }

  public downloadRespondentListTemplate(): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.downloadRespondentListTemplate(),
    );
  }

  public uploadRespondentList(file: File): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.uploadRespondentList({ file }),
    );
  }

  public toggleIncludeExcludeStatus(enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.toggleIncludeExcludeStatus({ enabled }),
    );
  }

  public resolveRespondentFilters(request: RespondentFiltersRequest): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.resolveRespondentFilters(request),
    );
  }

  public updateRespondentStatuses(statuses: number[]): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.updateRespondentStatuses({ statuses }),
    );
  }

  public clearRespondentList(): void {
    this._store.dispatch(SurveyCreationLaunchActions.clearRespondentList());
  }

  public uploadUniqueLinkPsidFile(file: File): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.uploadUniqueLinkPsidFile({ file }),
    );
  }

  public downloadUniqueLinkPsidTemplate(): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.downloadUniqueLinkPsidTemplate(),
    );
  }

  public clearUniqueLinkPsidFile(): void {
    this._store.dispatch(SurveyCreationLaunchActions.clearUniqueLinkPsidFile());
  }

  public logLaunchFieldToggled(
    surveyId: number,
    field: LaunchToggleField,
    enabled: boolean,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'launchToggle', field, enabled },
      }),
    );
  }

  public logAidaToggled(surveyId: number, enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'aidaToggled', enabled },
      }),
    );
  }

  public logUniqueBuyerEntryLinksActivity(
    surveyId: number,
    status: 'uploaded' | 'cleared',
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger:
          status === 'uploaded'
            ? { kind: 'uniqueBuyerEntryLinksUploaded' }
            : { kind: 'uniqueBuyerEntryLinksCleared' },
      }),
    );
  }

  public logEmailNotificationChanged(
    surveyId: number,
    from: string,
    to: string,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'emailNotificationChanged', from, to },
      }),
    );
  }

  public logExternalSurveyIdChanged(
    surveyId: number,
    externalSurveyId: string,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'externalSurveyIdChanged', externalSurveyId },
      }),
    );
  }

  public restoreSavedGrouping(savedSurveyIds: number[]): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.restoreSavedGrouping({ savedSurveyIds }),
    );
  }

  public logDateRangeFilterApplied(
    surveyId: number,
    { start, end }: DateRangeFilterParams,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: {
          kind: 'dateRangeFilterApplied',
          from: start.toISOString(),
          to: end.toISOString(),
        },
      }),
    );
  }

  public logSurveyGroupingUpdated(surveyId: number): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'surveyGroupingUpdated' },
      }),
    );
  }

  public logSelectedProjectManagerChanged(
    surveyId: number,
    from: string,
    to: string,
  ): void {
    this._store.dispatch(
      SurveyCreationLaunchActions.logSurveyLaunchActivity({
        surveyId,
        trigger: { kind: 'selectedProjectManager', from, to },
      }),
    );
  }

  public loadQualifications(localeData: SurveyLocaleData[]): void {
    for (const eachLocaleData of localeData) {
      this._store.dispatch(
        SurveyCreationReferenceActions.loadQualifications(eachLocaleData),
      );
    }
  }

  public loadQuestionCategories(): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestionCategories(),
    );
  }

  public loadQuestions(
    search: string,
    categoryIds: number[],
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestions({
        search,
        categoryIds,
        locale,
      }),
    );
  }

  public loadDependencyParentQuestion(
    parentQualificationId: number,
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadDependencyParentQuestion({
        parentQualificationId,
        locale,
      }),
    );
  }

  public loadLocationConditions(
    dbQualificationCode: number,
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadLocationConditions({
        dbQualificationCode,
        locale,
      }),
    );
  }

  public toggleManualProcessing(companyId: number, enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.toggleManualProcessing({
        companyId,
        enabled,
      }),
    );
  }

  public applyCpiPricing(
    pricing: SurveyCreationCpiPricing,
    scope: SurveyChildScope = CHILD_SCOPE.ACTIVE,
  ): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.applyCpiPricing({ ...pricing, scope }),
    );
  }

  public applyOfferPrice(apply: OfferPriceApply): void {
    this._store.dispatch(SurveyCreationInsightsActions.applyOfferPrice(apply));
  }

  public setSuppliersMetaData(metaData: Partial<SuppliersMetaData>): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.setSuppliersMetaData({ metaData }),
    );
  }

  public findSurveys(params: FindSurveysParams): void {
    this._store.dispatch(AdvancedFilterActions.findSurveys({ params }));
  }

  public getRespondentCounts(params: RespondentCountsParams): void {
    this._store.dispatch(AdvancedFilterActions.getRespondentCounts({ params }));
  }

  public searchCriteriaSurveys(params: SearchCriteriaSurveysParams): void {
    this._store.dispatch(
      AdvancedFilterActions.searchCriteriaSurveys({ params }),
    );
  }

  public searchGroupSurveys(params: SearchGroupSurveysParams): void {
    this._store.dispatch(AdvancedFilterActions.searchGroupSurveys({ params }));
  }

  public clearSurveyOptions(): void {
    this._store.dispatch(AdvancedFilterActions.clearSurveyOptions());
  }

  public applyAdvancedFilters(
    filters: AdvancedFeasibilityFilters,
    clearUploadedPsidsFor: Nullable<number>,
  ): void {
    this._store.dispatch(
      SurveyCreationAudienceActions.applyAdvancedFilters({
        filters,
        clearUploadedPsidsFor,
      }),
    );
  }

  public resetAdvancedFilters(): void {
    this._store.dispatch(AdvancedFilterActions.resetFilters());
  }

  public resyncSimilarSurveys(): void {
    this._store.dispatch(SurveyCreationInsightsActions.resyncSimilarSurveys());
  }

  public searchSimilarSurveysOutsideAccount(): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.searchSimilarSurveysOutsideAccount(),
    );
  }

  public cancelSimilarSurveysSearch(): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.cancelSimilarSurveysSearch(),
    );
  }

  public calculateFeasibilityForPreDraft(): void {
    this._store.dispatch(
      SurveyCreationInsightsPreDraftActions.calculateFeasibilityForPreDraft(),
    );
  }
}

// Actions stay serializable, so an absent key is left off rather than carried as an Option.
function activeChildKeyProp(activeChildKey: Option<SurveyChildKey>): {
  activeChildKey?: SurveyChildKey;
} {
  return activeChildKey.mapOr({}, (key) => ({ activeChildKey: key }));
}
